import { useState, useEffect, useCallback, useRef } from "react";

// ── 상수 ────────────────────────────────────────────────────────────────────
const KMA_API_KEY = "ac247916bd22e6e7e4fb8e93ab8d81c6e14b37c02d7280cad29e175c3085b26c";
const TELEGRAM_BOT_TOKEN = "8947209107:AAGf3DWEpd1rn28_oLqNesJypbX22pi5T0U";

// 텔레그램 다중 수신자 전송 (recipients 배열로 전체 발송)
async function sendTelegramAlert(message, recipients) {
  if (!TELEGRAM_BOT_TOKEN || TELEGRAM_BOT_TOKEN === "YOUR_TELEGRAM_BOT_TOKEN") {
    console.log("[텔레그램 시뮬레이션]", message, "→ 수신자:", recipients.length, "명");
    return recipients.map(r => ({ ok: true, simulated: true, name: r.name }));
  }
  return Promise.all(
    recipients.map(async (r) => {
      try {
        const res = await fetch(`https://api.telegram.org/bot${TELEGRAM_BOT_TOKEN}/sendMessage`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ chat_id: r.chatId, text: message, parse_mode: "HTML" }),
        });
        const data = await res.json();
        return { ...data, name: r.name };
      } catch (e) {
        return { ok: false, error: e.message, name: r.name };
      }
    })
  );
}

// 텔레그램 봇으로부터 /start 한 사용자 목록 자동 조회
async function fetchTelegramUpdates() {
  if (!TELEGRAM_BOT_TOKEN || TELEGRAM_BOT_TOKEN === "YOUR_TELEGRAM_BOT_TOKEN") return [];
  try {
    const res = await fetch(`https://api.telegram.org/bot${TELEGRAM_BOT_TOKEN}/getUpdates?limit=100`);
    const data = await res.json();
    if (!data.ok) return [];
    const seen = new Map();
    data.result.forEach(u => {
      const user = u.message?.from || u.callback_query?.from;
      const chatId = u.message?.chat?.id || u.callback_query?.message?.chat?.id;
      if (user && chatId && !seen.has(chatId)) {
        seen.set(chatId, {
          chatId: String(chatId),
          name: [user.first_name, user.last_name].filter(Boolean).join(" ") || user.username || String(chatId),
          username: user.username || "",
        });
      }
    });
    return Array.from(seen.values());
  } catch { return []; }
}
// 산업안전보건법 온열질환 예방 기준 (체감온도 기준) — 고용노동부 공식 기준
const HEAT_LEVELS = {
  공통: {
    min: 31, max: 33,
    color: "#22C55E", bg: "#F0FDF4", border: "#BBF7D0", emoji: "🟩",
    label: "공통사항 (폭염주의보)",
    tempRange: "31°C 이상",
    actions: [
      "근로자 폭염정보 제공",
      "시원한 물, 그늘(휴식공간), 바람 준비",
      "쿨토시 등 보냉장구 제공",
      "고강도 작업자와 민감군 관리",
    ],
    telegramAlert: false,
  },
  주의: {
    min: 33, max: 35,
    color: "#EAB308", bg: "#FEFCE8", border: "#FEF08A", emoji: "🟨",
    label: "주의 (폭염주의보)",
    tempRange: "33°C 이상",
    actions: [
      "매시간 10분씩 휴식 제공",
      "무더위 시간대 옥외작업 단축 또는 작업시간대 조정",
    ],
    telegramAlert: true,
  },
  경고: {
    min: 35, max: 38,
    color: "#F97316", bg: "#FFF7ED", border: "#FED7AA", emoji: "🟧",
    label: "경고 (폭염경보)",
    tempRange: "35°C 이상",
    actions: [
      "매시간 15분씩 휴식 제공",
      "무더위 시간대 불가피한 경우 제외하고 옥외작업 중지",
    ],
    telegramAlert: true,
  },
  위험: {
    min: 38, max: 999,
    color: "#EF4444", bg: "#FEF2F2", border: "#FECACA", emoji: "🟥",
    label: "위험 (폭염경보)",
    tempRange: "38°C 이상",
    actions: [
      "매시간 15분씩 휴식 제공",
      "무더위 시간대 재난·안전관리 작업 외 옥외작업 중지",
    ],
    telegramAlert: true,
  },
};

// 물류센터 데이터 (27개 전국 센터)
const LOGISTICS_CENTERS = [
  { id:  1, name: "강릉물류센터",   lat: 37.6768, lon: 128.8958, nx: 93, ny: 130, workers: 0, zone: "강원" },
  { id:  2, name: "원주물류센터",   lat: 37.2981, lon: 127.8712, nx: 75, ny: 121, workers: 0, zone: "강원" },
  { id:  3, name: "발안물류센터",   lat: 37.1895, lon: 126.8450, nx: 58, ny: 119, workers: 0, zone: "경기" },
  { id:  4, name: "남사물류센터",   lat: 37.1580, lon: 127.1990, nx: 64, ny: 118, workers: 0, zone: "경기" },
  { id:  5, name: "신오산물류센터", lat: 37.1498, lon: 127.0523, nx: 61, ny: 118, workers: 0, zone: "경기" },
  { id:  6, name: "화성물류센터",   lat: 37.1640, lon: 126.9870, nx: 60, ny: 118, workers: 0, zone: "경기" },
  { id:  7, name: "용인물류센터",   lat: 37.2820, lon: 127.2150, nx: 64, ny: 121, workers: 0, zone: "경기" },
  { id:  8, name: "고양물류센터",   lat: 37.6780, lon: 126.8140, nx: 57, ny: 129, workers: 0, zone: "경기" },
  { id:  9, name: "포천물류센터",   lat: 37.8940, lon: 127.2000, nx: 64, ny: 134, workers: 0, zone: "경기" },
  { id: 10, name: "송파물류센터",   lat: 37.5042, lon: 127.1144, nx: 62, ny: 125, workers: 0, zone: "서울" },
  { id: 11, name: "곤지암물류센터", lat: 37.3640, lon: 127.3710, nx: 67, ny: 122, workers: 0, zone: "경기" },
  { id: 12, name: "신남양주물류센터", lat: 37.6290, lon: 127.3380, nx: 66, ny: 128, workers: 0, zone: "경기" },
  { id: 13, name: "고촌물류센터",   lat: 37.6190, lon: 126.7280, nx: 56, ny: 128, workers: 0, zone: "경기" },
  { id: 14, name: "김포물류센터",   lat: 37.6190, lon: 126.7280, nx: 56, ny: 128, workers: 0, zone: "경기" },
  { id: 15, name: "인천물류센터",   lat: 37.4600, lon: 126.6580, nx: 54, ny: 124, workers: 0, zone: "인천" },
  { id: 16, name: "경산물류센터",   lat: 35.8720, lon: 128.7810, nx: 92, ny:  91, workers: 0, zone: "경북" },
  { id: 17, name: "김해물류센터",   lat: 35.2890, lon: 128.8320, nx: 93, ny:  78, workers: 0, zone: "경남" },
  { id: 18, name: "양산물류센터",   lat: 35.3480, lon: 129.0190, nx: 97, ny:  79, workers: 0, zone: "경남" },
  { id: 19, name: "진주물류센터",   lat: 35.1540, lon: 128.1130, nx: 81, ny:  75, workers: 0, zone: "경남" },
  { id: 20, name: "칠곡물류센터",   lat: 36.1120, lon: 128.4350, nx: 86, ny:  96, workers: 0, zone: "경북" },
  { id: 21, name: "부산물류센터",   lat: 35.1780, lon: 128.9740, nx: 96, ny:  76, workers: 0, zone: "부산" },
  { id: 22, name: "신제주물류센터", lat: 33.4620, lon: 126.3780, nx: 50, ny:  37, workers: 0, zone: "제주" },
  { id: 23, name: "공주물류센터",   lat: 36.4270, lon: 127.1050, nx: 62, ny: 102, workers: 0, zone: "충남" },
  { id: 24, name: "청주물류센터",   lat: 36.5810, lon: 127.3680, nx: 67, ny: 105, workers: 0, zone: "충북" },
  { id: 25, name: "익산물류센터",   lat: 35.9370, lon: 127.0460, nx: 61, ny:  91, workers: 0, zone: "전북" },
  { id: 26, name: "장성물류센터",   lat: 35.2980, lon: 126.7150, nx: 56, ny:  77, workers: 0, zone: "전남" },
  { id: 27, name: "신광주물류센터", lat: 35.1760, lon: 126.7940, nx: 57, ny:  75, workers: 0, zone: "광주" },
];

// 체감온도 계산 (Steadman 공식)
function calcFeelsLike(temp, humidity, windSpeed) {
  // 고온 체감온도 (Heat Index) - 27°C 이상일 때
  if (temp >= 27) {
    const T = temp;
    const R = humidity;
    const HI =
      -8.78469475556 +
      1.61139411 * T +
      2.338548839 * R -
      0.14611605 * T * R -
      0.012308094 * T * T -
      0.016424828 * R * R +
      0.002211732 * T * T * R +
      0.00072546 * T * R * R -
      0.000003582 * T * T * R * R;
    return Math.round(HI * 10) / 10;
  }
  // 저온 체감온도 (Wind Chill) - 10°C 이하, 바람 있을 때
  if (temp <= 10 && windSpeed > 1.3) {
    const WC = 13.12 + 0.6215 * temp - 11.37 * Math.pow(windSpeed * 3.6, 0.16) + 0.3965 * temp * Math.pow(windSpeed * 3.6, 0.16);
    return Math.round(WC * 10) / 10;
  }
  return temp;
}

function getHeatLevel(feelsLike) {
  if (feelsLike >= 38) return "위험";
  if (feelsLike >= 35) return "경고";
  if (feelsLike >= 33) return "주의";
  if (feelsLike >= 31) return "공통";
  return "정상";
}

const NORMAL_LEVEL = { color: "#7BA5D8", bg: "#162B50", border: "#274878", emoji: "⬜", label: "정상", tempRange: "31°C 미만", actions: ["정상 작업 가능", "수시로 수분 보충"], telegramAlert: false };

// 기상청 초단기실황 API 호출
async function fetchKMAWeather(nx, ny) {
  const now = new Date();
  const baseDate = now.toISOString().slice(0, 10).replace(/-/g, "");
  const hour = now.getHours();
  const minute = now.getMinutes();
  // 기상청은 매 시간 40분 이후에 데이터 제공
  const baseTime = minute < 40 ? `${String(hour - 1).padStart(2, "0")}00` : `${String(hour).padStart(2, "0")}00`;

  const url = `https://apis.data.go.kr/1360000/VilageFcstInfoService_2.0/getUltraSrtNcst?serviceKey=${KMA_API_KEY}&numOfRows=10&pageNo=1&dataType=JSON&base_date=${baseDate}&base_time=${baseTime}&nx=${nx}&ny=${ny}`;

  try {
    const res = await fetch(url);
    const data = await res.json();
    const items = data.response?.body?.items?.item || [];
    const result = {};
    items.forEach((item) => {
      result[item.category] = parseFloat(item.obsrValue);
    });
    // T1H: 기온, REH: 습도, WSD: 풍속, RN1: 1시간 강수량, PTY: 강수형태
    return {
      temp: result["T1H"] ?? null,
      humidity: result["REH"] ?? null,
      windSpeed: result["WSD"] ?? null,
      rain: result["RN1"] ?? 0,
      precipType: result["PTY"] ?? 0,
    };
  } catch (e) {
    // API 키 미설정 시 시뮬레이션 데이터 반환 (폭염 시나리오: 31~42°C)
    const baseTemp = 31 + Math.random() * 11;
    return {
      temp: Math.round(baseTemp * 10) / 10,
      humidity: Math.round(60 + Math.random() * 30),
      windSpeed: Math.round((0.5 + Math.random() * 3) * 10) / 10,
      rain: 0,
      precipType: 0,
      simulated: true,
    };
  }
}

// ── 역할별 수신자 필터링 후 알림 발송
// role: "전체관리자" → 모든 센터 수신
// role: "센터관리자"  → assignedCenters 배열에 포함된 센터만 수신
function getTargetRecipients(recipients, centerId) {
  return recipients.filter(r => {
    if (!r.active) return false;
    if (r.role === "전체관리자") return true;
    if (r.role === "센터관리자") return Array.isArray(r.assignedCenters) && r.assignedCenters.includes(centerId);
    return false;
  });
}

// ── 메인 컴포넌트 ────────────────────────────────────────────────────────────
export default function HeatstrokeDashboard() {
  const [centers, setCenters] = useState(LOGISTICS_CENTERS.map((c) => ({ ...c, weather: null, loading: true })));
  const [lastUpdated, setLastUpdated] = useState(null);
  const [alertLog, setAlertLog] = useState([]);
  const [historyData, setHistoryData] = useState({});
  const [activeTab, setActiveTab] = useState("dashboard");
  const [selectedCenter, setSelectedCenter] = useState(null);
  const [mapReady, setMapReady] = useState(false);
  const [notifications, setNotifications] = useState([]);
  // 수신자 관리 (role + assignedCenters 포함)
  const [recipients, setRecipients] = useState(() => {
    try { return JSON.parse(localStorage.getItem("tg_recipients_v2") || "[]"); } catch { return []; }
  });
  const [newRecipient, setNewRecipient] = useState({ name: "", chatId: "", role: "전체관리자", assignedCenters: [] });
  const [recipientError, setRecipientError] = useState("");
  const [fetchingUsers, setFetchingUsers] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const historyTimerRef = useRef(null);
  const alertedRef = useRef(new Set());

  // 날씨 데이터 수집
  const fetchAllWeather = useCallback(async () => {
    const updated = await Promise.all(
      LOGISTICS_CENTERS.map(async (center) => {
        const w = await fetchKMAWeather(center.nx, center.ny);
        const feelsLike = w.temp !== null ? calcFeelsLike(w.temp, w.humidity, w.windSpeed) : null;
        const level = feelsLike !== null ? getHeatLevel(feelsLike) : null;
        return { ...center, weather: { ...w, feelsLike, level }, loading: false };
      })
    );
    setCenters(updated);
    setLastUpdated(new Date());

    // 텔레그램 알림 (주의/경고/위험 시)
    updated.forEach((c) => {
      if (!c.weather?.level) return;
      const alertKey = `${c.id}-${c.weather.level}-${new Date().getHours()}`;
      const lvlData = HEAT_LEVELS[c.weather.level];
      const targetRecipients = getTargetRecipients(recipients, c.id);
      if (lvlData?.telegramAlert && !alertedRef.current.has(alertKey)) {
        alertedRef.current.add(alertKey);
        const actionLines = lvlData.actions.map((a) => `  • ${a}`).join("\n");
        const msg = `🚨 <b>[온열질환 ${c.weather.level} — ${lvlData.tempRange}]</b>\n\n📦 ${c.name}\n🌡️ 기온: ${c.weather.temp}°C\n🤒 체감온도: <b>${c.weather.feelsLike}°C</b>\n💧 습도: ${c.weather.humidity}%\n\n📋 <b>조치사항:</b>\n${actionLines}\n\n⏰ ${new Date().toLocaleTimeString("ko-KR")}`;
        if (targetRecipients.length > 0) sendTelegramAlert(msg, targetRecipients);
        setAlertLog((prev) => [{ id: Date.now(), center: c.name, centerId: c.id, level: c.weather.level, feelsLike: c.weather.feelsLike, time: new Date(), msg, recipientCount: targetRecipients.length }, ...prev.slice(0, 49)]);
        addNotification(`${c.name}: ${c.weather.level} (${c.weather.feelsLike}°C)`, c.weather.level);
      }
    });

    // 히스토리 저장 (1시간 평균 계산용)
    const now = new Date();
    const hourKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")} ${String(now.getHours()).padStart(2, "0")}:00`;
    setHistoryData((prev) => {
      const next = { ...prev };
      updated.forEach((c) => {
        if (!c.weather?.temp) return;
        if (!next[c.id]) next[c.id] = {};
        if (!next[c.id][hourKey]) next[c.id][hourKey] = { temps: [], feels: [], humidities: [], count: 0 };
        next[c.id][hourKey].temps.push(c.weather.temp);
        next[c.id][hourKey].feels.push(c.weather.feelsLike);
        next[c.id][hourKey].humidities.push(c.weather.humidity);
        next[c.id][hourKey].count += 1;
      });
      return next;
    });

    return updated;
  }, []);

  const addNotification = (message, level) => {
    const id = Date.now();
    setNotifications((prev) => [{ id, message, level }, ...prev.slice(0, 4)]);
    setTimeout(() => setNotifications((prev) => prev.filter((n) => n.id !== id)), 6000);
  };

  // 수신자 목록 localStorage 동기화
  useEffect(() => {
    try { localStorage.setItem("tg_recipients_v2", JSON.stringify(recipients)); } catch {}
  }, [recipients]);

  const addRecipient = () => {
    const name = newRecipient.name.trim();
    const chatId = newRecipient.chatId.trim().replace(/\s/g, "");
    if (!name) { setRecipientError("이름을 입력하세요."); return; }
    if (!chatId || !/^-?\d+$/.test(chatId)) { setRecipientError("Chat ID는 숫자여야 합니다."); return; }
    if (recipients.find(r => r.chatId === chatId)) { setRecipientError("이미 등록된 Chat ID입니다."); return; }
    if (newRecipient.role === "센터관리자" && newRecipient.assignedCenters.length === 0) {
      setRecipientError("센터관리자는 담당 센터를 1개 이상 선택해야 합니다."); return;
    }
    setRecipients(prev => [...prev, {
      id: Date.now(), name, chatId,
      role: newRecipient.role,
      assignedCenters: newRecipient.role === "전체관리자" ? [] : newRecipient.assignedCenters,
      active: true,
      addedAt: new Date().toLocaleString("ko-KR"),
    }]);
    setNewRecipient({ name: "", chatId: "", role: "전체관리자", assignedCenters: [] });
    setRecipientError("");
  };

  const removeRecipient = (id) => setRecipients(prev => prev.filter(r => r.id !== id));
  const toggleRecipient = (id) => setRecipients(prev => prev.map(r => r.id === id ? { ...r, active: !r.active } : r));

  const autoFetchUsers = async () => {
    setFetchingUsers(true);
    const users = await fetchTelegramUpdates();
    if (users.length === 0) {
      setRecipientError("봇에게 메시지를 보낸 사용자가 없거나 봇 토큰이 미설정입니다.");
    } else {
      let added = 0;
      users.forEach(u => {
        if (!recipients.find(r => r.chatId === u.chatId)) {
          setRecipients(prev => [...prev, {
            id: Date.now() + Math.random(), ...u,
            role: "전체관리자", assignedCenters: [], active: true,
            addedAt: new Date().toLocaleString("ko-KR"),
          }]);
          added++;
        }
      });
      setRecipientError(added === 0 ? "새로운 사용자가 없습니다." : "");
    }
    setFetchingUsers(false);
  };

  const sendTestMessage = async (recipient) => {
    const scope = recipient.role === "전체관리자"
      ? "전국 27개 물류센터 전체"
      : `담당 센터: ${(recipient.assignedCenters || []).map(id => LOGISTICS_CENTERS.find(c => c.id === id)?.name || id).join(", ")}`;
    const msg = `✅ <b>[테스트 알림]</b>\n\n안녕하세요, <b>${recipient.name}</b>님!\n온열질환 모니터링 시스템 알림이 정상 연결되었습니다.\n\n🎯 수신 범위: ${scope}\n⏰ ${new Date().toLocaleString("ko-KR")}`;
    await sendTelegramAlert(msg, [recipient]);
  };

  const toggleCenterAssign = (recipientId, centerId) => {
    setRecipients(prev => prev.map(r => {
      if (r.id !== recipientId) return r;
      const has = r.assignedCenters.includes(centerId);
      return { ...r, assignedCenters: has ? r.assignedCenters.filter(c => c !== centerId) : [...r.assignedCenters, centerId] };
    }));
  };
  useEffect(() => {
    const script = document.createElement("script");
    script.src = `//dapi.kakao.com/v2/maps/sdk.js?appkey=b1045031cacd02ea095013dc6e1f6a3d&autoload=false`;
    script.onload = () => {
      window.kakao?.maps?.load(() => setMapReady(true));
    };
    document.head.appendChild(script);
    return () => document.head.removeChild(script);
  }, []);

  // 지도 초기화
  useEffect(() => {
    if (!mapReady || activeTab !== "map" || !mapRef.current) return;
    const { kakao } = window;
    if (!kakao?.maps) return;
    const map = new kakao.maps.Map(mapRef.current, {
      center: new kakao.maps.LatLng(36.5, 127.5),
      level: 8,
    });
    markersRef.current.forEach((m) => m.setMap(null));
    markersRef.current = [];
    centers.forEach((c) => {
      if (!c.weather) return;
      const lvl = HEAT_LEVELS[c.weather.level] || NORMAL_LEVEL;
      const content = `<div style="background:${lvl.color};color:#fff;padding:6px 10px;border-radius:8px;font-size:12px;font-weight:700;white-space:nowrap;box-shadow:0 2px 8px rgba(0,0,0,.3)">${c.weather.level} ${c.weather.feelsLike}°C</div>`;
      const overlay = new kakao.maps.CustomOverlay({
        position: new kakao.maps.LatLng(c.lat, c.lon),
        content,
        yAnchor: 1,
      });
      overlay.setMap(map);
      markersRef.current.push(overlay);
    });
  }, [mapReady, activeTab, centers]);

  // 초기 로드 및 5분마다 갱신
  useEffect(() => {
    fetchAllWeather();
    const interval = setInterval(fetchAllWeather, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchAllWeather]);

  // 히스토리용 1시간 타이머
  useEffect(() => {
    const now = new Date();
    const msUntilNextHour = (60 - now.getMinutes()) * 60000 - now.getSeconds() * 1000;
    historyTimerRef.current = setTimeout(() => {
      historyTimerRef.current = setInterval(fetchAllWeather, 3600000);
    }, msUntilNextHour);
    return () => { clearTimeout(historyTimerRef.current); clearInterval(historyTimerRef.current); };
  }, [fetchAllWeather]);

  // 레벨별 집계
  const levelCounts = { 정상: 0, 공통: 0, 주의: 0, 경고: 0, 위험: 0 };
  centers.forEach((c) => { if (c.weather?.level) levelCounts[c.weather.level] = (levelCounts[c.weather.level] || 0) + 1; });

  // 시간별 평균 계산
  const getHourlyAvg = (centerId) => {
    const data = historyData[centerId] || {};
    return Object.entries(data).map(([hour, d]) => ({
      hour,
      avgTemp: Math.round((d.temps.reduce((a, b) => a + b, 0) / d.temps.length) * 10) / 10,
      avgFeels: Math.round((d.feels.reduce((a, b) => a + b, 0) / d.feels.length) * 10) / 10,
      avgHumidity: Math.round(d.humidities.reduce((a, b) => a + b, 0) / d.humidities.length),
      count: d.count,
    })).sort((a, b) => a.hour.localeCompare(b.hour)).slice(-12);
  };

  // ── 회사 CI 컬러 팔레트 — 물류봇 브랜드 (네이비 블루 ↔ 틸그린 그라디언트)
  // 이미지에서 추출: 좌측 딥네이비 #0C1F4A ~ 우측 틸그린 #0D4848
  const CI = {
    bg:          "#0F1F3D",   // 전체 배경 — 딥 네이비
    bgSurface:   "#162B50",   // 카드/패널 배경
    bgSurface2:  "#112240",   // 중첩 배경
    bgHeader:    "linear-gradient(105deg, #1A3A6E 0%, #135868 55%, #1A6068 100%)",
    bgTab:       "#111E38",   // 탭바
    border:      "#274878",   // 기본 보더
    border2:     "#1E3A68",   // 연한 보더
    accent:      "#4DA6FF",   // 블루 액센트 (물류봇 버튼색)
    accentDim:   "#2E86D4",
    text:        "#F0F6FF",   // 기본 텍스트
    textMuted:   "#7BA5D8",   // 흐린 텍스트
    textDim:     "#3A6090",   // 매우 흐린 텍스트
    live:        "#4ADEAA",   // 라이브 인디케이터
    btnBg:       "#1E3F70",
    btnBorder:   "#2E5EA0",
    scrollThumb: "#2A4E88",
  };

  return (
    <div style={{ fontFamily: "'Noto Sans KR', sans-serif", background: CI.bg, minHeight: "100vh", color: CI.text }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Noto+Sans+KR:wght@400;500;700;900&family=JetBrains+Mono:wght@400;700&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        ::-webkit-scrollbar { width: 6px; }
        ::-webkit-scrollbar-track { background: #0F1F3D; }
        ::-webkit-scrollbar-thumb { background: #2A4E88; border-radius: 4px; }
        .tab-btn { background: none; border: none; cursor: pointer; padding: 11px 22px; font-family: inherit; font-size: 14px; font-weight: 600; color: #7BA5D8; border-bottom: 2px solid transparent; transition: all .2s; }
        .tab-btn.active { color: #4DA6FF; border-color: #4DA6FF; }
        .tab-btn:hover:not(.active) { color: #B8D0F0; }
        .center-card { background: #162B50; border: 1px solid #274878; border-radius: 14px; padding: 16px; cursor: pointer; transition: all .2s; }
        .center-card:hover { border-color: #4DA6FF40; transform: translateY(-2px); box-shadow: 0 8px 28px rgba(0,0,0,.5), 0 0 0 1px #4DA6FF15; }
        .badge { display: inline-flex; align-items: center; gap: 4px; padding: 3px 10px; border-radius: 20px; font-size: 12px; font-weight: 700; }
        .pulse { animation: pulse 2s infinite; }
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:.4} }
        .notif { animation: slideIn .3s ease; }
        @keyframes slideIn { from{transform:translateX(110%);opacity:0} to{transform:translateX(0);opacity:1} }
        .stat-num { font-family: 'JetBrains Mono', monospace; }
        .map-container { width: 100%; height: 500px; border-radius: 14px; overflow: hidden; }
        .history-table { width: 100%; border-collapse: collapse; font-size: 13px; }
        .history-table th { background: #112240; padding: 10px 14px; text-align: left; font-weight: 600; color: #7BA5D8; border-bottom: 1px solid #274878; }
        .history-table td { padding: 10px 14px; border-bottom: 1px solid #274878; color: #C8E0FF; }
        .history-table tr:hover td { background: #162B50; }
        .bar { height: 6px; border-radius: 4px; transition: width .5s ease; }
        .alert-item { background: #162B50; border-left: 3px solid; padding: 10px 14px; border-radius: 0 8px 8px 0; margin-bottom: 8px; }
        .refresh-btn { background: #1E3F70; border: 1px solid #2E5EA0; color: #B8D0F0; padding: 8px 16px; border-radius: 10px; cursor: pointer; font-size: 13px; font-weight: 600; display: flex; align-items: center; gap: 6px; font-family: inherit; transition: all .2s; }
        .refresh-btn:hover { background: #2A5090; border-color: #4DA6FF; color: #4DA6FF; }
      `}</style>

      {/* 플로팅 알림 */}
      <div style={{ position: "fixed", top: 20, right: 20, zIndex: 9999, display: "flex", flexDirection: "column", gap: 8 }}>
        {notifications.map((n) => (
          <div key={n.id} className="notif" style={{ background: HEAT_LEVELS[n.level]?.color || "#4DA6FF", color: "#fff", padding: "12px 16px", borderRadius: 10, maxWidth: 300, fontSize: 13, fontWeight: 600, boxShadow: "0 4px 20px rgba(0,0,0,.5)" }}>
            🚨 {n.message}
          </div>
        ))}
      </div>

      {/* 헤더 */}
      <header style={{ background: "linear-gradient(105deg, #1A3A6E 0%, #135868 55%, #1A6068 100%)", borderBottom: "1px solid #27487880", padding: "18px 28px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <div style={{ width: 40, height: 40, background: "linear-gradient(135deg,#EF4444,#F97316)", borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, boxShadow: "0 3px 14px rgba(239,68,68,.4)" }}>🌡️</div>
          <div>
            <div style={{ fontSize: 19, fontWeight: 900, letterSpacing: "-0.5px", color: "#F0F6FF" }}>온열질환 모니터링 시스템</div>
            <div style={{ fontSize: 11, color: "#A8CCEE", marginTop: 3, letterSpacing: "0.2px" }}>Platform WMS · 물류센터 실시간 현황 · 산업안전보건법 기준</div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          {lastUpdated && (
            <div style={{ fontSize: 12, color: "#7BA5D8", display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ color: "#4ADEAA", fontSize: 8 }}>●</span>
              마지막 갱신: <strong style={{ color: "#B8D0F0" }}>{lastUpdated.toLocaleTimeString("ko-KR")}</strong>
            </div>
          )}
          <button className="refresh-btn" onClick={fetchAllWeather}>
            🔄 새로고침
          </button>
        </div>
      </header>

      {/* 탭 */}
      <div style={{ background: "#111E38", borderBottom: "1px solid #274878", padding: "0 28px", display: "flex", gap: 2 }}>
        {[["dashboard", "📊 대시보드"], ["map", "🗺️ 지도"], ["history", "📈 이력 관리"], ["alerts", "🔔 알림 로그"]].map(([id, label]) => (
          <button key={id} className={`tab-btn ${activeTab === id ? "active" : ""}`} onClick={() => setActiveTab(id)}>{label}</button>
        ))}
      </div>

      <main style={{ padding: 24, maxWidth: 1400, margin: "0 auto" }}>

        {/* ── 대시보드 탭 ── */}
        {activeTab === "dashboard" && (
          <>
            {/* 레벨 요약 카드 */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(5,1fr)", gap: 14, marginBottom: 24 }}>
              {/* 정상 */}
              {(() => {
                const count = centers.filter(c => c.weather?.level === "정상").length;
                return (
                  <div style={{ background: "#162B50", border: "1px solid #274878", borderRadius: 12, padding: "16px 18px" }}>
                    <div style={{ fontSize: 11, color: "#7BA5D8", fontWeight: 700, marginBottom: 6 }}>⬜ 정상</div>
                    <div className="stat-num" style={{ fontSize: 34, fontWeight: 900, color: "#4DA6FF", lineHeight: 1 }}>{count}</div>
                    <div style={{ fontSize: 11, color: "#3A6090", marginTop: 4 }}>물류센터</div>
                    <div style={{ marginTop: 10, background: "#112240", borderRadius: 3, height: 4 }}>
                      <div className="bar" style={{ background: "#4DA6FF40", width: `${(count / centers.length) * 100}%` }} />
                    </div>
                    <div style={{ fontSize: 10, color: "#3A6090", marginTop: 4 }}>31°C 미만</div>
                  </div>
                );
              })()}
              {Object.entries(HEAT_LEVELS).map(([name, lvl]) => {
                const count = centers.filter(c => c.weather?.level === name).length;
                return (
                  <div key={name} style={{ background: "#162B50", border: `1px solid ${lvl.color}35`, borderRadius: 12, padding: "16px 18px", borderTop: `3px solid ${lvl.color}` }}>
                    <div style={{ fontSize: 11, color: lvl.color, fontWeight: 700, marginBottom: 6 }}>{lvl.emoji} {name}</div>
                    <div className="stat-num" style={{ fontSize: 34, fontWeight: 900, color: lvl.color, lineHeight: 1 }}>{count}</div>
                    <div style={{ fontSize: 11, color: "#7BA5D8", marginTop: 4 }}>물류센터</div>
                    <div style={{ marginTop: 10, background: "#112240", borderRadius: 3, height: 4 }}>
                      <div className="bar" style={{ background: lvl.color, width: `${(count / centers.length) * 100}%` }} />
                    </div>
                    <div style={{ fontSize: 10, color: lvl.color, marginTop: 4 }}>{lvl.tempRange}</div>
                  </div>
                );
              })}
            </div>

            {/* 물류센터 카드 그리드 */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 16 }}>
              {centers.map((c) => {
                const levelName = c.weather?.level;
                const lvl = (levelName && levelName !== "정상") ? HEAT_LEVELS[levelName] : NORMAL_LEVEL;
                return (
                  <div key={c.id} className="center-card" onClick={() => { setSelectedCenter(c); setActiveTab("history"); }} style={{ borderLeft: `3px solid ${lvl.color}` }}>
                    {c.loading ? (
                      <div style={{ display: "flex", alignItems: "center", gap: 8, color: "#7BA5D8" }}>
                        <div className="pulse">⏳</div> 데이터 로딩 중...
                      </div>
                    ) : c.weather ? (
                      <>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
                          <div>
                            <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 2, color: "#F0F6FF" }}>📦 {c.name}</div>
                            <div style={{ fontSize: 12, color: "#7BA5D8" }}>👥 근로자 {c.workers}명 · {c.zone}</div>
                          </div>
                          <span className="badge" style={{ background: `${lvl.color}20`, color: lvl.color }}>
                            {lvl.emoji} {levelName}
                          </span>
                        </div>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
                          {[
                            { label: "현재기온", val: `${c.weather.temp}°C`, icon: "🌡️" },
                            { label: "체감온도", val: `${c.weather.feelsLike}°C`, icon: "🤒", highlight: true },
                            { label: "습도", val: `${c.weather.humidity}%`, icon: "💧" },
                          ].map((stat) => (
                            <div key={stat.label} style={{ background: stat.highlight ? `${lvl.color}15` : "#112240", borderRadius: 8, padding: "10px 12px", border: stat.highlight ? `1px solid ${lvl.color}50` : "1px solid #274878" }}>
                              <div style={{ fontSize: 11, color: "#7BA5D8", marginBottom: 4 }}>{stat.icon} {stat.label}</div>
                              <div className="stat-num" style={{ fontSize: 18, fontWeight: 700, color: stat.highlight ? lvl.color : "#F0F6FF" }}>{stat.val}</div>
                            </div>
                          ))}
                        </div>
                        {/* 조치사항 */}
                        {lvl.actions && levelName !== "정상" && (
                          <div style={{ marginTop: 10, background: `${lvl.color}0C`, border: `1px solid ${lvl.color}30`, borderRadius: 8, padding: "10px 12px" }}>
                            <div style={{ fontSize: 11, color: lvl.color, fontWeight: 700, marginBottom: 6 }}>📋 조치사항</div>
                            {lvl.actions.map((a, i) => (
                              <div key={i} style={{ fontSize: 11, color: "#B8D0F0", display: "flex", gap: 6, marginBottom: i < lvl.actions.length - 1 ? 4 : 0 }}>
                                <span style={{ color: lvl.color, flexShrink: 0 }}>▶</span>{a}
                              </div>
                            ))}
                          </div>
                        )}
                        <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                          <div style={{ flex: 1, background: "#112240", borderRadius: 8, padding: "8px 12px", border: "1px solid #274878" }}>
                            <div style={{ fontSize: 11, color: "#7BA5D8" }}>💨 풍속</div>
                            <div className="stat-num" style={{ fontSize: 15, fontWeight: 700, color: "#F0F6FF" }}>{c.weather.windSpeed} m/s</div>
                          </div>
                          {c.weather.simulated && (
                            <div style={{ display: "flex", alignItems: "center", padding: "8px 10px", background: "#1E3F70", borderRadius: 8, fontSize: 11, color: "#7BA5D8", border: "1px solid #2E5EA0" }}>
                              🔄 시뮬레이션
                            </div>
                          )}
                        </div>
                      </>
                    ) : <div style={{ color: "#7BA5D8" }}>데이터 없음</div>}
                  </div>
                );
              })}
            </div>

            {/* 산업안전보건법 기준 안내 */}
            <div style={{ marginTop: 20, background: "#111827", border: "1px solid #274878", borderRadius: 12, padding: 20 }}>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 14, color: "#7BA5D8" }}>📋 고용노동부 온열질환 예방 조치기준 (체감온도)</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 12 }}>
                {Object.entries(HEAT_LEVELS).map(([name, lvl]) => (
                  <div key={name} style={{ background: "#112240", borderRadius: 10, padding: "14px 16px", borderTop: `3px solid ${lvl.color}` }}>
                    <div style={{ fontSize: 13, fontWeight: 800, color: lvl.color, marginBottom: 2 }}>{lvl.emoji} {name}</div>
                    <div style={{ fontSize: 11, color: "#7BA5D8", marginBottom: 8 }}>{lvl.label} · {lvl.tempRange}</div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                      {lvl.actions.map((a, i) => (
                        <div key={i} style={{ fontSize: 11, color: "#B8D0F0", display: "flex", gap: 5, lineHeight: 1.5 }}>
                          <span style={{ color: lvl.color, flexShrink: 0, marginTop: 1 }}>▶</span>
                          <span>{a}</span>
                        </div>
                      ))}
                    </div>
                    {lvl.telegramAlert && (
                      <div style={{ marginTop: 8, fontSize: 10, color: "#4DA6FF", background: "#4DA6FF12", padding: "3px 7px", borderRadius: 4, display: "inline-block" }}>
                        📱 텔레그램 자동 알림
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {/* ── 지도 탭 ── */}
        {activeTab === "map" && (
          <div>
            <div style={{ marginBottom: 16, display: "flex", gap: 10, flexWrap: "wrap" }}>
              {centers.map((c) => {
                const lvl2 = (c.weather?.level && c.weather.level !== "정상") ? HEAT_LEVELS[c.weather.level] : NORMAL_LEVEL;
                return (
                  <div key={c.id} style={{ background: "#162B50", border: `1px solid ${lvl2?.color}40`, borderRadius: 8, padding: "8px 14px", fontSize: 13 }}>
                    <span style={{ color: lvl2?.color || "#4DA6FF", fontWeight: 700 }}>{lvl2?.emoji} {c.name}</span>
                    {c.weather && <span style={{ color: "#7BA5D8", marginLeft: 8 }}>{c.weather.feelsLike}°C</span>}
                  </div>
                );
              })}
            </div>
            <div ref={mapRef} className="map-container" style={{ background: "#162B50", border: "1px solid #274878", display: "flex", alignItems: "center", justifyContent: "center", color: "#7BA5D8" }}>
              {!mapReady && <div style={{ textAlign: "center" }}>🗺️ 카카오맵 API 키를 설정하면 지도가 표시됩니다<br /><span style={{ fontSize: 12, color: "#3A6090" }}>dapi.kakao.com에서 API 키 발급</span></div>}
            </div>
            {!mapReady && (
              <div style={{ marginTop: 16, background: "#162B50", border: "1px solid #274878", borderRadius: 12, padding: 20 }}>
                <div style={{ fontSize: 13, color: "#7BA5D8", marginBottom: 12, fontWeight: 700 }}>📍 물류센터 위치 현황 (위경도 기준)</div>
                <div style={{ overflowX: "auto" }}>
                  <table className="history-table">
                    <thead>
                      <tr>
                        <th>물류센터</th><th>위도</th><th>경도</th><th>현재기온</th><th>체감온도</th><th>단계</th>
                      </tr>
                    </thead>
                    <tbody>
                      {centers.map((c) => {
                        const lvlT = (c.weather?.level && c.weather.level !== "정상") ? HEAT_LEVELS[c.weather.level] : NORMAL_LEVEL;
                        return (
                          <tr key={c.id}>
                            <td style={{ fontWeight: 600 }}>{c.name}</td>
                            <td style={{ fontFamily: "monospace" }}>{c.lat}</td>
                            <td style={{ fontFamily: "monospace" }}>{c.lon}</td>
                            <td className="stat-num">{c.weather?.temp ?? "-"}°C</td>
                            <td className="stat-num" style={{ color: lvlT?.color, fontWeight: 700 }}>{c.weather?.feelsLike ?? "-"}°C</td>
                            <td>{c.weather?.level && <span className="badge" style={{ background: `${lvlT.color}20`, color: lvlT.color }}>{lvlT.emoji} {c.weather.level}</span>}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── 이력 관리 탭 ── */}
        {activeTab === "history" && (
          <div>
            <div style={{ display: "flex", gap: 10, marginBottom: 20, flexWrap: "wrap" }}>
              <button onClick={() => setSelectedCenter(null)} style={{ background: !selectedCenter ? "#2E86D4" : "#1E3F70", border: `1px solid ${!selectedCenter ? "#2E86D4" : "#2E5EA0"}`, color: !selectedCenter ? "#0F1F3D" : "#B8D0F0", padding: "8px 16px", borderRadius: 8, cursor: "pointer", fontSize: 13, fontWeight: 700 }}>
                전체
              </button>
              {centers.map((c) => (
                <button key={c.id} onClick={() => setSelectedCenter(c)} style={{ background: selectedCenter?.id === c.id ? "#2E86D4" : "#1E3F70", border: `1px solid ${selectedCenter?.id === c.id ? "#2E86D4" : "#2E5EA0"}`, color: selectedCenter?.id === c.id ? "#0F1F3D" : "#B8D0F0", padding: "8px 16px", borderRadius: 8, cursor: "pointer", fontSize: 13, fontWeight: 700 }}>
                  {c.name}
                </button>
              ))}
            </div>

            {(selectedCenter ? [selectedCenter] : centers).map((c) => {
              const hourly = getHourlyAvg(c.id);
              return (
                <div key={c.id} style={{ background: "#162B50", border: "1px solid #274878", borderRadius: 12, padding: 20, marginBottom: 16 }}>
                  <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 16, color: "#F0F6FF" }}>📦 {c.name} — 1시간 단위 평균</div>
                  {hourly.length === 0 ? (
                    <div style={{ color: "#7BA5D8", fontSize: 13, padding: "20px 0", textAlign: "center" }}>
                      ⏳ 데이터 수집 중... (수집된 데이터가 없습니다)<br />
                      <span style={{ fontSize: 12 }}>시스템이 1시간 단위로 평균을 자동 계산하여 저장합니다</span>
                    </div>
                  ) : (
                    <div style={{ overflowX: "auto" }}>
                      <table className="history-table">
                        <thead>
                          <tr>
                            <th>시간대</th>
                            <th>평균기온</th>
                            <th>평균체감온도</th>
                            <th>평균습도</th>
                            <th>수집횟수</th>
                            <th>온열단계</th>
                          </tr>
                        </thead>
                        <tbody>
                          {hourly.map((row) => {
                            const level = getHeatLevel(row.avgFeels);
                            const lvl = HEAT_LEVELS[level] || NORMAL_LEVEL;
                            return (
                              <tr key={row.hour}>
                                <td className="stat-num" style={{ color: "#7BA5D8" }}>{row.hour}</td>
                                <td className="stat-num">{row.avgTemp}°C</td>
                                <td className="stat-num" style={{ color: lvl.color, fontWeight: 700 }}>{row.avgFeels}°C</td>
                                <td className="stat-num">{row.avgHumidity}%</td>
                                <td className="stat-num" style={{ color: "#7BA5D8" }}>{row.count}회</td>
                                <td><span className="badge" style={{ background: `${lvl.color}20`, color: lvl.color }}>{lvl.emoji} {level}</span></td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* 현재 데이터 미리보기 */}
                  {c.weather && (
                    <div style={{ marginTop: 12, padding: 12, background: "#112240", borderRadius: 8, fontSize: 12, color: "#7BA5D8", display: "flex", gap: 20, border: "1px solid #274878" }}>
                      <span>현재 수집 중: 기온 <strong style={{ color: "#F0F6FF" }}>{c.weather.temp}°C</strong></span>
                      <span>체감온도 <strong style={{ color: HEAT_LEVELS[c.weather.level]?.color || "#4DA6FF" }}>{c.weather.feelsLike}°C</strong></span>
                      <span>습도 <strong style={{ color: "#F0F6FF" }}>{c.weather.humidity}%</strong></span>
                      {c.weather.simulated && <span style={{ color: "#F59E0B" }}>⚠️ 시뮬레이션 데이터</span>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* ── 알림 탭 ── */}
        {activeTab === "alerts" && (
          <div style={{ display: "grid", gridTemplateColumns: "420px 1fr", gap: 20, alignItems: "start" }}>

            {/* ── 왼쪽: 수신자 관리 ── */}
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

              {/* 챗봇 안내 배너 */}
              <div style={{ background: "linear-gradient(105deg,#1A3A6E,#135868)", border: "1px solid #4DA6FF40", borderRadius: 14, padding: 18 }}>
                <div style={{ fontWeight: 800, fontSize: 14, color: "#F0F6FF", marginBottom: 8 }}>🤖 챗봇으로 Chat ID 자동 확인</div>
                <div style={{ fontSize: 12, color: "#A8CCEE", lineHeight: 1.7, marginBottom: 12 }}>
                  텔레그램에서 봇을 찾아 <strong style={{ color: "#4ADEAA" }}>/start</strong> 를 입력하면<br/>
                  봇이 본인의 Chat ID를 자동으로 알려줍니다.<br/>
                  그 번호를 아래에 붙여넣기하세요.
                </div>
                <button onClick={autoFetchUsers} disabled={fetchingUsers} style={{ background: "#1E3F70", border: "1px solid #4DA6FF60", color: "#4DA6FF", padding: "8px 16px", borderRadius: 9, cursor: "pointer", fontSize: 12, fontWeight: 700, fontFamily: "inherit", width: "100%" }}>
                  {fetchingUsers ? "⏳ 조회 중..." : "🔄 봇 대화자 자동 불러오기"}
                </button>
              </div>

              {/* 수신자 등록 폼 */}
              <div style={{ background: "#162B50", border: "1px solid #274878", borderRadius: 14, padding: 20 }}>
                <div style={{ fontWeight: 700, fontSize: 14, color: "#F0F6FF", marginBottom: 16 }}>➕ 수신자 등록</div>

                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {/* 이름 */}
                  <div>
                    <div style={{ fontSize: 11, color: "#7BA5D8", marginBottom: 4 }}>이름</div>
                    <input placeholder="예: 홍길동 안전관리자" value={newRecipient.name}
                      onChange={e => setNewRecipient(p => ({ ...p, name: e.target.value }))}
                      style={{ width: "100%", background: "#112240", border: "1px solid #274878", borderRadius: 8, padding: "9px 12px", color: "#F0F6FF", fontSize: 13, outline: "none", fontFamily: "inherit" }} />
                  </div>

                  {/* Chat ID */}
                  <div>
                    <div style={{ fontSize: 11, color: "#7BA5D8", marginBottom: 4 }}>
                      Chat ID
                      <span style={{ color: "#4ADEAA", marginLeft: 6 }}>← 봇에서 /start 후 받은 숫자</span>
                    </div>
                    <input placeholder="예: 123456789" value={newRecipient.chatId}
                      onChange={e => setNewRecipient(p => ({ ...p, chatId: e.target.value }))}
                      style={{ width: "100%", background: "#112240", border: "1px solid #274878", borderRadius: 8, padding: "9px 12px", color: "#F0F6FF", fontSize: 13, outline: "none", fontFamily: "monospace" }} />
                  </div>

                  {/* 역할 선택 */}
                  <div>
                    <div style={{ fontSize: 11, color: "#7BA5D8", marginBottom: 6 }}>역할</div>
                    <div style={{ display: "flex", gap: 8 }}>
                      {["전체관리자", "센터관리자"].map(role => (
                        <button key={role} onClick={() => setNewRecipient(p => ({ ...p, role, assignedCenters: [] }))}
                          style={{ flex: 1, padding: "9px 0", borderRadius: 9, cursor: "pointer", fontSize: 13, fontWeight: 700, fontFamily: "inherit", border: "2px solid", transition: "all .15s",
                            background: newRecipient.role === role ? (role === "전체관리자" ? "#1E3F70" : "#2A1A40") : "#112240",
                            borderColor: newRecipient.role === role ? (role === "전체관리자" ? "#4DA6FF" : "#A78BFA") : "#274878",
                            color: newRecipient.role === role ? (role === "전체관리자" ? "#4DA6FF" : "#A78BFA") : "#7BA5D8",
                          }}>
                          {role === "전체관리자" ? "🌐 전체관리자" : "📦 센터관리자"}
                        </button>
                      ))}
                    </div>
                    <div style={{ fontSize: 11, color: "#7BA5D8", marginTop: 6, padding: "6px 10px", background: "#112240", borderRadius: 6 }}>
                      {newRecipient.role === "전체관리자"
                        ? "✅ 전국 27개 물류센터 경보 전체 수신"
                        : "⚠️ 아래에서 담당 센터를 선택하세요"}
                    </div>
                  </div>

                  {/* 센터관리자: 담당 센터 선택 */}
                  {newRecipient.role === "센터관리자" && (
                    <div>
                      <div style={{ fontSize: 11, color: "#7BA5D8", marginBottom: 6 }}>담당 센터 선택 ({newRecipient.assignedCenters.length}개 선택됨)</div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, maxHeight: 160, overflowY: "auto", padding: 4 }}>
                        {LOGISTICS_CENTERS.map(c => {
                          const on = newRecipient.assignedCenters.includes(c.id);
                          return (
                            <button key={c.id} onClick={() => setNewRecipient(p => ({
                              ...p,
                              assignedCenters: on ? p.assignedCenters.filter(x => x !== c.id) : [...p.assignedCenters, c.id]
                            }))} style={{ padding: "4px 10px", borderRadius: 6, cursor: "pointer", fontSize: 11, fontWeight: 600, fontFamily: "inherit", border: "1px solid", transition: "all .15s",
                              background: on ? "#2A1A40" : "#112240",
                              borderColor: on ? "#A78BFA" : "#274878",
                              color: on ? "#C4B5FD" : "#7BA5D8",
                            }}>
                              {c.name.replace("물류센터", "")}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {recipientError && <div style={{ fontSize: 11, color: "#F87171", padding: "6px 10px", background: "#EF444420", borderRadius: 6 }}>⚠️ {recipientError}</div>}

                  <button onClick={addRecipient} style={{ background: "linear-gradient(105deg,#1A3A6E,#135868)", border: "1px solid #4DA6FF50", color: "#F0F6FF", padding: "10px 0", borderRadius: 10, cursor: "pointer", fontSize: 14, fontWeight: 700, fontFamily: "inherit" }}>
                    + 수신자 등록
                  </button>
                </div>
              </div>

              {/* 등록된 수신자 목록 */}
              <div style={{ background: "#162B50", border: "1px solid #274878", borderRadius: 14, padding: 20 }}>
                <div style={{ fontWeight: 700, fontSize: 14, color: "#F0F6FF", marginBottom: 14, display: "flex", justifyContent: "space-between" }}>
                  <span>등록된 수신자</span>
                  <span style={{ fontSize: 12, color: "#7BA5D8", fontWeight: 400 }}>
                    전체관리자 {recipients.filter(r => r.role === "전체관리자").length}명 · 센터관리자 {recipients.filter(r => r.role === "센터관리자").length}명
                  </span>
                </div>
                {recipients.length === 0 ? (
                  <div style={{ textAlign: "center", color: "#7BA5D8", fontSize: 13, padding: "20px 0" }}>등록된 수신자가 없습니다</div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {recipients.map(r => {
                      const isGlobal = r.role === "전체관리자";
                      const assignedNames = (r.assignedCenters || []).map(id => LOGISTICS_CENTERS.find(c => c.id === id)?.name?.replace("물류센터", "") || id);
                      return (
                        <div key={r.id} style={{ background: "#112240", border: `1px solid ${r.active ? (isGlobal ? "#4DA6FF30" : "#A78BFA30") : "#274878"}`, borderRadius: 10, padding: "12px 14px", opacity: r.active ? 1 : 0.5 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            {/* 활성 토글 */}
                            <div onClick={() => toggleRecipient(r.id)} style={{ width: 36, height: 20, borderRadius: 10, background: r.active ? (isGlobal ? "#1E3F70" : "#2A1A40") : "#1E2A40", border: `2px solid ${r.active ? (isGlobal ? "#4DA6FF" : "#A78BFA") : "#274878"}`, cursor: "pointer", position: "relative", flexShrink: 0 }}>
                              <div style={{ width: 12, height: 12, borderRadius: "50%", background: r.active ? (isGlobal ? "#4DA6FF" : "#A78BFA") : "#4A6090", position: "absolute", top: 2, left: r.active ? 18 : 2, transition: "left .2s" }} />
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2 }}>
                                <span style={{ fontWeight: 700, fontSize: 13, color: "#F0F6FF" }}>{r.name}</span>
                                <span style={{ fontSize: 10, padding: "2px 7px", borderRadius: 10, fontWeight: 700,
                                  background: isGlobal ? "#1E3F70" : "#2A1A40",
                                  color: isGlobal ? "#4DA6FF" : "#A78BFA",
                                  border: `1px solid ${isGlobal ? "#4DA6FF50" : "#A78BFA50"}`,
                                }}>{isGlobal ? "🌐 전체" : "📦 센터"}</span>
                              </div>
                              <div style={{ fontSize: 11, color: "#7BA5D8", fontFamily: "monospace" }}>ID: {r.chatId}</div>
                              {!isGlobal && assignedNames.length > 0 && (
                                <div style={{ fontSize: 10, color: "#A78BFA", marginTop: 3 }}>
                                  {assignedNames.join(" · ")}
                                </div>
                              )}
                            </div>
                            <div style={{ display: "flex", gap: 5, flexShrink: 0 }}>
                              <button onClick={() => sendTestMessage(r)} title="테스트 발송" style={{ background: "#1E3F70", border: "1px solid #2E5EA0", color: "#4DA6FF", width: 28, height: 28, borderRadius: 7, cursor: "pointer", fontSize: 12 }}>✉</button>
                              <button onClick={() => removeRecipient(r.id)} title="삭제" style={{ background: "#3A1A1A", border: "1px solid #7A2020", color: "#F87171", width: 28, height: 28, borderRadius: 7, cursor: "pointer", fontSize: 12 }}>✕</button>
                            </div>
                          </div>
                          {/* 센터관리자: 담당 센터 인라인 편집 */}
                          {!isGlobal && editingId === r.id && (
                            <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px solid #274878" }}>
                              <div style={{ fontSize: 11, color: "#A78BFA", marginBottom: 6 }}>담당 센터 편집</div>
                              <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                                {LOGISTICS_CENTERS.map(c => {
                                  const on = (r.assignedCenters || []).includes(c.id);
                                  return (
                                    <button key={c.id} onClick={() => toggleCenterAssign(r.id, c.id)}
                                      style={{ padding: "3px 8px", borderRadius: 5, cursor: "pointer", fontSize: 10, fontWeight: 600, fontFamily: "inherit", border: "1px solid",
                                        background: on ? "#2A1A40" : "#112240", borderColor: on ? "#A78BFA" : "#274878", color: on ? "#C4B5FD" : "#7BA5D8",
                                      }}>
                                      {c.name.replace("물류센터", "")}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                          {!isGlobal && (
                            <button onClick={() => setEditingId(editingId === r.id ? null : r.id)}
                              style={{ marginTop: 8, background: "none", border: "none", color: "#7BA5D8", fontSize: 11, cursor: "pointer", padding: 0, fontFamily: "inherit" }}>
                              {editingId === r.id ? "▲ 접기" : "✏️ 담당 센터 편집"}
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* ── 오른쪽: 알림 발송 이력 ── */}
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: "#F0F6FF" }}>🔔 알림 발송 이력</div>
                <div style={{ fontSize: 13, color: "#7BA5D8" }}>총 {alertLog.length}건</div>
              </div>
              {alertLog.length === 0 ? (
                <div style={{ background: "#162B50", border: "1px solid #274878", borderRadius: 14, padding: 48, textAlign: "center", color: "#7BA5D8" }}>
                  <div style={{ fontSize: 36, marginBottom: 12 }}>✅</div>
                  <div style={{ fontSize: 15, fontWeight: 600, color: "#B8D0F0" }}>아직 발생한 알림이 없습니다</div>
                  <div style={{ fontSize: 13, marginTop: 6 }}>체감온도 33°C 이상 시 역할에 따라 자동 발송됩니다</div>
                </div>
              ) : alertLog.map((a) => {
                const lvl = HEAT_LEVELS[a.level] || NORMAL_LEVEL;
                return (
                  <div key={a.id} className="alert-item" style={{ borderColor: lvl.color }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span className="badge" style={{ background: `${lvl.color}20`, color: lvl.color }}>{lvl.emoji} {a.level}</span>
                        <span style={{ fontWeight: 700, color: "#F0F6FF" }}>{a.center}</span>
                        <span style={{ color: "#7BA5D8", fontSize: 13 }}>체감 {a.feelsLike}°C</span>
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 3 }}>
                        <div style={{ fontSize: 11, color: "#7BA5D8" }}>{a.time.toLocaleString("ko-KR")}</div>
                        <div style={{ fontSize: 11, color: "#4ADEAA" }}>📱 {a.recipientCount}명 수신</div>
                      </div>
                    </div>
                    <div style={{ fontSize: 12, color: "#7BA5D8", background: "#112240", padding: "8px 12px", borderRadius: 6, fontFamily: "monospace", whiteSpace: "pre-wrap", border: "1px solid #274878" }}>{a.msg}</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </main>

      {/* 푸터 */}
      <footer style={{ borderTop: "1px solid #274878", padding: "16px 24px", display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 12, color: "#3A6090", background: "#0F1F3D" }}>
        <div>기상청 초단기실황 API · 산업안전보건법 제24조 온열질환 예방 기준 · Platform WMS</div>
        <div>5분마다 자동 갱신 · 1시간 단위 평균 저장</div>
      </footer>
    </div>
  );
}
